import {SlChangeEvent, SlColorPicker} from '@shoelace-style/shoelace';
import {isEmpty, kebabCase} from 'lodash';

import {camelToKebab, getRemValue, getTailwindSpacingValue} from '../../../utils/tailwindcss';
import {updateASTComponentProp} from '../../../store/common/action';
import {
  ALIGN_CONTENT,
  ALIGN_ITEMS,
  ALIGN_SELF,
  ANIMATION,
  BD_BL_RADIUS,
  BD_BOTTOM_COLOR,
  BD_BOTTOM_WIDTH,
  BD_BR_RADIUS,
  BD_COLOR,
  BD_LEFT_COLOR,
  BD_LEFT_WIDTH,
  BD_RADIUS,
  BD_RIGHT_COLOR,
  BD_RIGHT_WIDTH,
  BD_STYLE,
  BD_TL_RADIUS,
  BD_TOP_COLOR,
  BD_TOP_WIDTH,
  BD_TR_RADIUS,
  BD_WIDTH,
  BG_COLOR,
  BG_IMAGE,
  BG_REPEAT,
  BOX_SHADOW,
  BOX_SIZING,
  COLOR,
  COLUMN_GAP,
  CURSOR,
  DISPLAY,
  FLEX,
  FLEX_BASIS,
  FLEX_DIRECTION,
  FLEX_GROW,
  FLEX_SHRINK,
  FLEX_WRAP,
  FONT_FAMILY,
  FONT_SIZE,
  FONT_STYLE,
  FONT_WEIGHT,
  GRID_COLUMN,
  GRID_COLUMN_END,
  GRID_COLUMN_START,
  GRID_ROW,
  GRID_ROW_END,
  GRID_ROW_START,
  GRID_TEMPLATE_COLUMNS,
  GRID_TEMPLATE_ROWS,
  HEIGHT,
  JUSTIFY_CONTENT,
  LETTER_SPACING,
  MASK_COMPOSITE,
  MASK_CLIP,
  MAX_HEIGHT,
  MAX_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH,
  OPACITY,
  ORDER,
  OVERFLOW,
  OVERFLOW_X,
  OVERFLOW_Y,
  POSITION,
  ROTATE,
  ROW_GAP,
  SIMPLIFY,
  TAILWIND_PREFIX,
  TEXT_ALIGN,
  TEXT_WRAP,
  TRANSITION_BEHAVIOR,
  TRANSITION_DELAY,
  TRANSITION_DURATION,
  TRANSITION_PROPERTY,
  TRANSITION_TIMING_FUNCTION,
  VERTICAL_ALIGN,
  WIDTH,
  Z_INDEX,
  MASK_MODE,
  MASK_ORIGIN,
  MASK_POSITION,
  MASK_REPEAT,
  MASK_SIZE,
  MASK_TYPE,
  MASK_IMAGE,
  BACKGROUND_IMAGE,
  BACKGROUND_REPEAT,
  BACKGROUND_SIZE,
  MARGIN_TOP, MARGIN_RIGHT, MARGIN_BOTTOM, MARGIN_LEFT, SCROLLBAR, SCROLLBAR_WIDTH, SCROLLBAR_THUMB,
  SCROLL_BEHAVIOR, SNAP, SCROLL_SNAP_ALIGN
} from './constant';
import {MetaInfo} from './type';

/**
 * 生成组件唯一ID.
 *
 * @param identifier - 组件标识.
 * @returns 唯一ID值.
 */
export function genId(identifier: string) {
  return `${this.metaInfo.type}-${identifier}`;
}

/**
 * 生成状态前缀.
 */
export function genStatusPrefix(metaInfo: MetaInfo): string {
  let statusPrefix = '';
  if (metaInfo.type === 'status' && metaInfo.bind === 'status') {
    statusPrefix = `${metaInfo.value}:`;
  }

  return statusPrefix;
}

/**
 * 从tailwind类名中解析出有效值.
 *
 * @param key - 属性名.
 * @param tailwindValue - tailwind类名.
 */
export function parseTailwindValue(key: string, tailwindValue: string): string {
  if (!key || !tailwindValue) {
    return '';
  }

  if([MARGIN_TOP, MARGIN_RIGHT, MARGIN_BOTTOM, MARGIN_LEFT].includes(key)  && ['auto'].includes(tailwindValue)){
    const prefix = TAILWIND_PREFIX[key];
    return tailwindValue.replace(prefix ? `${prefix}-` : '', '');
  }

  if ([MASK_IMAGE, BACKGROUND_IMAGE].indexOf(key) != -1) {   //    去掉去前后的 []

    const prefix = TAILWIND_PREFIX[key];
    if (tailwindValue.endsWith("none")){    // 空图片
      return tailwindValue.replace(prefix ? `${prefix}-` : '', '');
    }else if(tailwindValue.startsWith(`${prefix}-[url(`)) {    // 图片
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-[` : '', '');
      return tailwindValue.slice(0, -1);
    }else {    // 渐变颜色
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-[` : '', '').split("_").join(" ");
      return tailwindValue.slice(0, -1);
    }
  }

  if ([BACKGROUND_SIZE].indexOf(key) != -1) {   //    去掉去前后的 []
    const prefix = TAILWIND_PREFIX[key];
    tailwindValue = tailwindValue.replace(prefix ? `${prefix}-[` : '', '').replace(prefix ? `]` : '', '').split("_").join(" ");
    return tailwindValue;
  }

  if ([BG_COLOR].indexOf(key) != -1 && tailwindValue != null && tailwindValue.startsWith("bg-[rgba(")) {   //    去掉去前后的 []
    const prefix = TAILWIND_PREFIX[key];
    tailwindValue = tailwindValue.replace(prefix ? `${prefix}-[` : '', '').split("_").join(" ");
    return tailwindValue.slice(0, -1);
  }

  if ([BACKGROUND_REPEAT].indexOf(key) != -1 && tailwindValue != null ) {   //    去掉去前后的 []
    const prefix = TAILWIND_PREFIX[key];
    if(["bg-repeat-space", "bg-repeat-round"].indexOf(tailwindValue) != -1){
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-repeat-` : '', '');
    }else {
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-` : '', '');
    }
    return tailwindValue;
  }

  if ([MASK_CLIP, MASK_ORIGIN].indexOf(key) != -1) {
    const prefix = TAILWIND_PREFIX[key];
    if (tailwindValue != "mask-no-clip") {
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-` : '', '');
      tailwindValue = tailwindValue + "-box";
    } else {
      tailwindValue = "no-clip";
    }
    return tailwindValue;
  }

  if ([MASK_COMPOSITE, MASK_TYPE, MASK_SIZE].indexOf(key) != -1) {
    const prefix = TAILWIND_PREFIX[key];
    return tailwindValue.replace(prefix ? `${prefix}-` : '', '');
  }

  if (key == MASK_MODE) {
    const prefix = TAILWIND_PREFIX[key];
    if (tailwindValue == "mask-match") {
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-` : '', '') + "-source";
    } else {
      tailwindValue = tailwindValue.replace(prefix ? `${prefix}-` : '', '');
    }
    return tailwindValue;
  }

  if (key == MASK_POSITION) {
    const prefix = TAILWIND_PREFIX[key];
    tailwindValue = tailwindValue.replace(prefix ? `${prefix}-` : '', '');
    tailwindValue = tailwindValue.replace('-', ' ');
    return tailwindValue;
  }

  if (key == MASK_REPEAT) {
    const prefix = TAILWIND_PREFIX[key];

    tailwindValue = tailwindValue.replace(prefix ? `${prefix}-repeat-` : '', '');
    tailwindValue = tailwindValue.replace(prefix ? `${prefix}-` : '', '');
    return tailwindValue;
  }

  // 对枚举属性值的处理.
  const enumValue = [
    FLEX_DIRECTION, FLEX_WRAP, JUSTIFY_CONTENT, ALIGN_ITEMS, ALIGN_CONTENT, FLEX, ALIGN_SELF,
    TRANSITION_PROPERTY, TRANSITION_BEHAVIOR, TRANSITION_TIMING_FUNCTION,
    DISPLAY, OVERFLOW, CURSOR
  ];
  if (enumValue.includes(key)) {
    const prefix = TAILWIND_PREFIX[key];
    return tailwindValue.replace(prefix ? `${prefix}-` : '', '');
  }

  // 对非斜体字体的特殊处理.
  if (key === FONT_STYLE && tailwindValue.includes('not-italic')) {
    return 'normal';
  }

  // 对background-repeat的特殊处理.
  if (key === BG_REPEAT && (tailwindValue.includes('bg-repeat-x') || tailwindValue.includes('bg-repeat-y') || tailwindValue.includes('bg-no-repeat'))) {
    return tailwindValue.replace('bg-', '');
  }

  // 对background-image的特殊处理.
  if (key === BG_IMAGE) {
    let tempVal: string = tailwindValue.split('bg-')[1].replace(/^\[/, '').replace(/\]$/, '');

    if (tailwindValue.includes('bg-[linear-gradient(') || tailwindValue.includes('bg-[radial-gradient(')) {
      tempVal = tempVal.replace(/_/g, '');
    }

    return tempVal;
  }
  // 判断tailwindValue 是否是数字
  if (typeof tailwindValue === 'number' && !isNaN(tailwindValue)) {
    return tailwindValue;
  }
  // 先取出最后一段值.
  let value = tailwindValue.split('-').at(-1).split(':').at(-1);

  // 去掉值外围包裹的中括号"[]".
  value = value.replace(/^\[/, '').replace(/\]$/, '');

  // 负值处理.
  if (tailwindValue.includes('-[-') || tailwindValue.startsWith('-')) {
    value = `-${value}`;
  }

  // border-width的简写处理.
  if ([BD_WIDTH, BD_TOP_WIDTH, BD_RIGHT_WIDTH, BD_BOTTOM_WIDTH, BD_LEFT_WIDTH].includes(key) && value === 'border') {
    return '1';
  }

  // 从PP解析过来的阴影值hack处理.
  if (key === BOX_SHADOW) {
    const hackRegExp = new RegExp(/(?<=shadow:)(inset_)?\dpx_\dpx_\dpx_\dpx_rgba\(\d+,\d+,\d+,\d+(.\d+)?\)(?=.*)/);
    const hackMatched = value.match(hackRegExp);

    if (hackMatched) {
      return _get(hackMatched, 0);
    } else {
      if (tailwindValue.includes('inset-shadow')) {
        return `inset_${value}`;
      }
    }
  }

  // 对 border-radius: none; 的特殊处理.
  if ([BD_RADIUS, BD_TL_RADIUS, BD_TR_RADIUS, BD_BR_RADIUS, BD_BL_RADIUS].includes(key) && value === 'none') {
    return '0';
  }

  // 纯数字.
  const pureNumberRegExp = new RegExp(/^[-+]?\d+(\.\d+)?$/);

  if (pureNumberRegExp.test(value)) {
    const borderWidth = [BD_WIDTH, BD_TOP_WIDTH, BD_RIGHT_WIDTH, BD_BOTTOM_WIDTH, BD_LEFT_WIDTH];
    const grid = [GRID_TEMPLATE_COLUMNS, GRID_TEMPLATE_ROWS, GRID_COLUMN, GRID_COLUMN_START, GRID_COLUMN_END, GRID_ROW, GRID_ROW_START, GRID_ROW_END, COLUMN_GAP, ROW_GAP,];
    const flexNumber = [ORDER, FLEX_GROW, FLEX_SHRINK];
    const transitionNumber = [TRANSITION_DURATION, TRANSITION_DELAY];

    if ([ROTATE, OPACITY, Z_INDEX, ...borderWidth, ...grid, ...flexNumber, ...transitionNumber].includes(key)) {
      return value;
    }

    // 值是否为0.5的倍数.
    const isHalfMultiple: boolean = parseFloat(value) % 0.5 === 0;
    // 0.5的倍数值可以只用tailwind间距表示.
    if (isHalfMultiple) {
      const varSpacing = parseFloat(getTailwindSpacingValue());
      return `${parseFloat(value) * varSpacing}`;
    }
  }

  // 带单位，形如xxpx.
  const unitRegExp = new RegExp(/.+(?=(px|%|rem|deg)$)/);
  const unitMatched = value.match(unitRegExp);

  if (unitMatched) {
    const unitMatchedValue = _get(unitMatched, 0);
    const unit: string = _get(unitMatched, 1);

    if (unit === 'px') {
      if (![BD_RADIUS, BD_TL_RADIUS, BD_TR_RADIUS, BD_BR_RADIUS, BD_BL_RADIUS, BD_WIDTH, BD_TOP_WIDTH, BD_RIGHT_WIDTH, BD_BOTTOM_WIDTH, BD_LEFT_WIDTH].includes(key)) {
        const rem = parseFloat(getRemValue());
        // 像素px到rem的换算.
        return `${unitMatchedValue / rem}`;
      }
    }

    return unitMatchedValue;
  }

  return value;
}

/**
 * 解析tailwind值，设计态
 * @param key
 * @param tailwindValue
 */
export function parseTailwindValueDesign(key: string, tailwindValue: string): string {
  let value = parseTailwindValue(key, tailwindValue);
  // 对响应式简写值的处理.
  if (SIMPLIFY[value] && [WIDTH, MIN_WIDTH, MAX_WIDTH, FLEX_BASIS].includes(key)) {
    value = SIMPLIFY[value];
  }
  return value;
}

/**
 * 获取样式属性在dynamicCss中的取值路径.
 *
 * @param [key] - 样式名.
 * @returns 点“.”分隔的路径字符串，供lodash的get、set方法使用.
 */
export function getValuePath(key?: string): string {
  if (!this.dynamicCss) {
    return;
  }
  const dynamicCssIndex = this.dynamicCss.findIndex(dynamicCss => dynamicCss.bind === this.metaInfo.bind);

  if (dynamicCssIndex === -1) {
    return '';
  }

  let sourceIndex = this.dynamicCss[dynamicCssIndex].sources.findIndex(source => source.value === this.metaInfo.value && source.expression === this.metaInfo.expression);

  if (this.metaInfo.index) {
    sourceIndex = this.metaInfo.index;
  }


  if (sourceIndex === -1) {
    return '';
  }

  return `${dynamicCssIndex}.sources.${sourceIndex}.css.${this.metaInfo.kind}${key ? `.${camelToKebab(key)}` : ''}`;
}

/**
 * 封装样式属性初始化回显的回调.
 *
 * @param key - 样式名.
 */
export function initValue(key: string,) {
  const tailwindValue = _get(this.dynamicCss, getValuePath.call(this, key), '');

  // 空字符串和包含unset的字符串被视为不合法值.
  const isValid: boolean = tailwindValue !== '' && !tailwindValue.includes('unset');
  // 合法值进一步解析，不合法值直接赋值空字符串.
  if (isValid) {
    this[key] = parseTailwindValue(key, tailwindValue);
  } else {
    this[key] = '';
  }
  return this[key];
}

/**
 * 封装样式属性change回调的通用方法.
 *
 * @param selector - css选择器，指定样式属性组件的dom元素.
 * @param key - 样式名.
 * @param tailwindPrefix - tailwind类名前缀.
 */
export function handleChange(selector: string, key: string, tailwindPrefix: string) {
  const element: HTMLElement = document.querySelector(selector);

  element.addEventListener('sl-change', (event: SlChangeEvent) => {
    const value = _get(event, 'target.value');

    if (value === '') {
      const oldValue = this[key];

      setTimeout(() => {
        this[key] = oldValue;
      }, 0);
    } else {
      setStyleValue.call(this, key, value, tailwindPrefix, event);
    }

    this[key] = value;
  });
}

/**
 * 设置样式属性.
 *
 * @param key - 样式名.
 * @param value - 样式值.
 * @param tailwindPrefix - 前缀.
 * @param event - .
 */
export function setStyleValue(key: string, value: string, tailwindPrefix: string, event?) {
  const path: string = `dynamicCss.${getValuePath.call(this, key)}`;

  let updatedValue: string;

  const borderWidth = [BD_WIDTH, BD_TOP_WIDTH, BD_RIGHT_WIDTH, BD_BOTTOM_WIDTH, BD_LEFT_WIDTH];
  const grid = [GRID_TEMPLATE_COLUMNS, GRID_TEMPLATE_ROWS, GRID_COLUMN, GRID_COLUMN_START, GRID_COLUMN_END, GRID_ROW, GRID_ROW_START, GRID_ROW_END, COLUMN_GAP, ROW_GAP];
  const flex = [FLEX_DIRECTION, FLEX_WRAP, JUSTIFY_CONTENT, ALIGN_ITEMS, ALIGN_CONTENT, FLEX, ALIGN_SELF, ORDER, FLEX_GROW, FLEX_SHRINK];
  const transition = [TRANSITION_PROPERTY, TRANSITION_BEHAVIOR, TRANSITION_DURATION, TRANSITION_DELAY, TRANSITION_TIMING_FUNCTION];
  const overflow = [OVERFLOW, OVERFLOW_X, OVERFLOW_Y];
  const scrollbar = [SCROLLBAR, SCROLLBAR_WIDTH, SCROLLBAR_THUMB, SCROLL_BEHAVIOR, SNAP, SCROLL_SNAP_ALIGN];
  const cursor = [CURSOR,];
  const textWrap = [TEXT_WRAP];
  const maskArr = [MASK_COMPOSITE, MASK_MODE, MASK_REPEAT, MASK_SIZE, MASK_TYPE];

  if ([WIDTH, HEIGHT, MIN_WIDTH, MAX_WIDTH, MIN_HEIGHT, MAX_HEIGHT].includes(key)) {
    // 宽高
    if ([WIDTH, HEIGHT].includes(key) && ['full', 'auto', 'screen'].includes(value)) {
      updatedValue = value
    } else if ([MIN_WIDTH, MAX_WIDTH, MIN_HEIGHT, MAX_HEIGHT].includes(key) && ['full', 'screen'].includes(value)) {
      updatedValue = value
    } else if (isValidPositiveFraction(value)) {
      // 处理分数
      updatedValue = value
    } else if ([WIDTH, MIN_WIDTH, MAX_WIDTH].includes(key) && SIMPLIFY[value]) {
      // 枚举类
      updatedValue = value
    } else {
      updatedValue = getDefaultValue(value)
    }
  } else if ([BD_RADIUS, BD_TL_RADIUS, BD_TR_RADIUS, BD_BR_RADIUS, BD_BL_RADIUS].includes(key)) {
    // 圆角
    updatedValue = `[${value}px]`;
  } else if ([BOX_SIZING, DISPLAY, POSITION, OPACITY, ...borderWidth, ...maskArr, BD_STYLE, TEXT_ALIGN, VERTICAL_ALIGN, FONT_WEIGHT, ...grid, ...flex, ...transition, ...overflow,...scrollbar, ...cursor, ...textWrap, ANIMATION].includes(key)) {
    // 盒子
    updatedValue = value;
  } else if ([Z_INDEX, FONT_FAMILY].includes(key)) {
    // 层级 和 字体
    updatedValue = `[${value}]`;
  }  else if ([MASK_IMAGE, BACKGROUND_IMAGE].includes(key)) {  // 背景图片 和 蒙版图片
    if (value == "none"){   // 空图片
      updatedValue = `${value}`;
    }else if(value.startsWith("url(")) {    // 背景图片
      updatedValue = `[${value}]`;
    }else {   // 渐变颜色
      updatedValue = `[${value}]`.split(" ").join("_");
    }

  } else if ([ROTATE].includes(key)) {
    // 角度
    updatedValue = `[${value}deg]`;
  } else if ([COLOR, BG_COLOR, BD_COLOR, BD_TOP_COLOR, BD_RIGHT_COLOR, BD_BOTTOM_COLOR, BD_LEFT_COLOR].includes(key)) {
    if (event != undefined) {
      if((event.target as HTMLElement).tagName === 'SL-SELECT'){  // 这种标签的特殊处理，2026-2-2目前在 background.tsx下使用
        updatedValue = `[${value}]`.split(" ").join("_");
      }else {
        // 颜色
        const rgbaValue = (event.target as SlColorPicker).getFormattedValue('rgba');
        updatedValue = `[${rgbaValue.replace(/\s/g, '')}]`;
      }
    }
  } else if ([FONT_SIZE, LETTER_SPACING].includes(key)) {
    // 字体大小
    updatedValue = `[${value}rem]`;
  }  else if (MASK_CLIP == key || MASK_ORIGIN == key ) {
    updatedValue = value.replace("-box", "");

  }else if(MASK_POSITION == key){
    updatedValue = value.replace(" ", "-").replace("_", "-");  //前面有可能把空格转成下划线“_”
  }else if(BACKGROUND_REPEAT == key){
    if (["space","round"].indexOf(value) != -1){
      updatedValue = "repeat-" + value;
    }else {
      updatedValue = value;
    }
  } else if(BACKGROUND_SIZE == key){
    updatedValue = `[${value}]`.split(" ").join("_");
  }  else if([MARGIN_TOP, MARGIN_RIGHT, MARGIN_BOTTOM, MARGIN_LEFT].includes(key)  && ['auto'].includes(value)){
    updatedValue = value;
  } else {
    updatedValue = getDefaultValue(value);
  }
  const statusPrefix = genStatusPrefix(this.metaInfo);
  let tailwindValue = tailwindPrefix ? `${statusPrefix}${tailwindPrefix}-${updatedValue}` : `${statusPrefix}${updatedValue}`;

  if (key == MASK_CLIP && value == "no-clip") {
    tailwindValue = "mask-no-clip";
  }
  if (key == MASK_MODE && value == "match-source") {
    tailwindValue = "mask-match";
  }

  if(MASK_REPEAT == key && ['space', "round"].indexOf(value) != -1){
    tailwindValue = "mask-repeat-" + value;
  }

  updateASTComponentProp(this.data, path, tailwindValue);

  // 使用flex布局实现文本对齐.
  if (key === TEXT_ALIGN) {
    fixAlign.call(this, JUSTIFY_CONTENT, updatedValue, 'justify');
  }
  if (key === VERTICAL_ALIGN) {
    fixAlign.call(this, ALIGN_ITEMS, updatedValue, 'items');
  }
}

/**
 * 获取默认值
 * @param value
 */
export function getDefaultValue(value) {
  let updatedValue;
  const varSpacing = getTailwindSpacingValue();
  updatedValue = `${parseFloat(value) / parseFloat(varSpacing)}`;
  // 值是否为0.5的倍数.
  const isHalfMultiple: boolean = parseFloat(updatedValue) % 0.5 === 0;
  // 负值.
  const isNegativeValue = value.startsWith('-');
  // 非0.5的倍数值需要用rem表示.
  if (!isHalfMultiple || isNegativeValue) {
    updatedValue = `[${value}rem]`;
  }
  return updatedValue
}

/**
 * 修补文本对齐样式.
 *
 * @remark
 * 使用flex布局来实现文本对齐.
 *
 * @param key - 样式名.
 * @param originValue - 原对齐属性值.
 * @param tailwindPrefix - 前缀.
 */
export function fixAlign(key: string, originValue: string, tailwindPrefix: string) {
  let map = {};

  if (key === JUSTIFY_CONTENT) {
    map = {left: 'start', center: 'center', right: 'end'};
  } else if (key === ALIGN_ITEMS) {
    map = {top: 'start', middle: 'center', bottom: 'end'};
  }

  const value = map[originValue];

  if (value) {
    const statusPrefix = genStatusPrefix(this.metaInfo);
    const tailwindValue = tailwindPrefix ? `${statusPrefix}${tailwindPrefix}-${value}` : `${statusPrefix}${value}`;

    updateASTComponentProp(this.data, `dynamicCss.${getValuePath.call(this, DISPLAY)}`, `${statusPrefix}flex`);
    updateASTComponentProp(this.data, `dynamicCss.${getValuePath.call(this, key)}`, tailwindValue);
  }
}

/**
 * 封装样式属性（使用/禁用）按钮的回调.
 *
 * @param selector - css选择器，指定（使用/禁用）按钮的dom元素.
 * @param key - 样式名.
 * @param displayKey - （使用/禁用）按钮的变量名.
 * @param tailwindPrefix - tailwind类名前缀.
 */
export function handleDisplayIconClick(selector: string, key: string, displayKey: string, tailwindPrefix: string) {
  const element: HTMLElement = document.querySelector(selector);

  element.addEventListener('click', () => {
    this[displayKey] = !this[displayKey];

    const hasValidValue: boolean = this[displayKey] && !isEmpty(this[key]);

    if (hasValidValue) {
      setStyleValue.call(this, key, this[key], tailwindPrefix);
    } else {
      removeStyleValue.call(this, key);
    }
  });
}

/**
 * 删除样式属性.
 *
 * @param key - 样式名.
 */
export function removeStyleValue(key: string) {
  const path: string = `dynamicCss.${getValuePath.call(this)}`;
  const styles = _cloneDeep(_get(this.data, path, {}));

  delete styles[kebabCase(key)];

  updateASTComponentProp(this.data, path, styles);
}

/**
 * 验证你是否百分比
 * @param value
 */
export function isPercentage(value) {
  if (typeof value !== 'string') return false;
  return /^\d+(\.\d+)?%$/.test(value);
}

/**
 * 验证是否为严格意义上的正分数（必须包含分数线，分子分母都是正整数，分子不为0）
 * @param {string} fraction - 输入字符串
 * @returns {boolean} - 如果是有效的正分数，返回 true；否则返回 false
 */
export function isValidPositiveFraction(fraction) {
  if (!fraction || typeof fraction != 'string') {
    return false;
  }
  // 必须包含且只能包含一个分数线
  const parts = fraction.split('/');
  if (parts.length !== 2) {
    return false;
  }

  // 提取分子和分母
  const numeratorStr = parts[0].trim();
  const denominatorStr = parts[1].trim();

  // 验证分子是否为正整数（大于0的整数）
  const numerator = parseInt(numeratorStr);
  if (isNaN(numerator) || !Number.isInteger(numerator) ||
    numeratorStr !== numerator.toString() ||
    numerator <= 0) {
    return false;
  }

  // 验证分母是否为正整数
  const denominator = parseInt(denominatorStr);
  if (isNaN(denominator) || !Number.isInteger(denominator) ||
    denominatorStr !== denominator.toString() ||
    denominator <= 0) {
    return false;
  }

  return true;
}
